import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import { DEFAULT_SETTINGS, loadSettings, persistSettings } from '../utils/settings';

const SettingsPage = () => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [autoExpandEntries, setAutoExpandEntries] = useState([]);
  const [autoExpandSearch, setAutoExpandSearch] = useState('');
  const [isLoadingAutoExpand, setIsLoadingAutoExpand] = useState(false);

  useEffect(() => {
    setSettings(loadSettings());
  }, []);

  useEffect(() => {
    const fetchAutoExpand = async () => {
      setIsLoadingAutoExpand(true);
      try {
        const { data } = await api.get('/api/stt/auto-expand/defaults');
        setAutoExpandEntries(data);
      } catch (error) {
        console.error('Failed to load auto-expand defaults:', error);
        toast.error('Failed to load auto-expand defaults');
      } finally {
        setIsLoadingAutoExpand(false);
      }
    };

    fetchAutoExpand();
  }, []);

  const applySettings = (newSettings, options = {}) => {
    persistSettings(newSettings);
    setSettings(loadSettings());
    if (!options.silent) {
      toast.success('Settings saved');
    }
  };

  const handleToggle = (key) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    applySettings(newSettings);
  };

  const handleModeChange = (mode) => {
    const newSettings = { ...settings, defaultMode: mode };
    applySettings(newSettings);
  };

  const handleProviderChange = (provider) => {
    if (!provider) return;
    const newSettings = { ...settings, sttProvider: provider };
    applySettings(newSettings);
  };

  const handleAutoExpandToggle = (term, defaultValue) => {
    const canonical = term.toLowerCase();
    const overrides = { ...(settings.autoExpandOverrides || {}) };
    const current = Object.prototype.hasOwnProperty.call(overrides, canonical)
      ? overrides[canonical]
      : undefined;
    const nextValue = current !== undefined ? !current : !defaultValue;

    if (nextValue === defaultValue) {
      delete overrides[canonical];
    } else {
      overrides[canonical] = nextValue;
    }

    applySettings(
      {
        ...settings,
        autoExpandOverrides: overrides,
      },
      { silent: true }
    );
  };

  const handleResetOverrides = () => {
    if (!settings.autoExpandOverrides || Object.keys(settings.autoExpandOverrides).length === 0) {
      return;
    }
    applySettings(
      {
        ...settings,
        autoExpandOverrides: {},
      }
    );
  };

  const overrideCount = Object.keys(settings.autoExpandOverrides || {}).length;
  const overridesLocked = !(settings.enableRealtimeCorrections && settings.enableAutoExpand);
  const filteredAutoExpand = useMemo(() => {
    if (!autoExpandEntries.length) {
      return [];
    }
    const keyword = autoExpandSearch.trim().toLowerCase();
    return autoExpandEntries
      .filter((entry) => entry.term && entry.expansion)
      .filter((entry) => {
        if (!keyword) return true;
        return (
          entry.term.toLowerCase().includes(keyword) ||
          entry.expansion.toLowerCase().includes(keyword) ||
          (entry.category || '').toLowerCase().includes(keyword)
        );
      })
      .sort((a, b) => a.term.localeCompare(b.term));
  }, [autoExpandEntries, autoExpandSearch]);

  const providerKey = settings.sttProvider || 'azure';
  const providerCostCopy = {
    azure: {
      realtime: '~100ms latency • £1.05/hour • Best for live dictation',
      batch: 'Processing time • £0.80/hour • 23% cheaper',
    },
    speechmatics: {
      realtime: '~120ms latency • £0.90/hour • Supports flexible vocabularies',
      batch: 'Processing time • £0.72/hour • SaaS usage billed per minute',
    },
    sherpa: {
      realtime: '~100ms latency • £0.00/hour • Local GPU pipeline',
      batch: 'Processing time • £0.00/hour • Offloads to Sherpa batch',
    },
  };

  const realtimeCostLabel =
    providerCostCopy[providerKey]?.realtime ?? providerCostCopy.azure.realtime;

  const batchCostLabel =
    providerCostCopy[providerKey]?.batch ?? providerCostCopy.azure.batch;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Settings</h1>

      {/* Real-time Corrections Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          ✨ Real-time Corrections
        </h2>
        
        <div className="space-y-4">
          {/* Enable Real-time Corrections */}
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Enable Real-time Corrections</h3>
              <p className="text-sm text-gray-600">
                Apply corrections automatically as you dictate
              </p>
            </div>
            <button
              onClick={() => handleToggle('enableRealtimeCorrections')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.enableRealtimeCorrections ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.enableRealtimeCorrections ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Auto-expand Abbreviations */}
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Auto-expand Abbreviations</h3>
              <p className="text-sm text-gray-600">
                Automatically expand medical abbreviations (BP → blood pressure)
              </p>
              <p className="text-xs text-blue-600 mt-1">
                ⚡ Instant • No cost • Highly recommended
              </p>
            </div>
            <button
              onClick={() => handleToggle('enableAutoExpand')}
              disabled={!settings.enableRealtimeCorrections}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.enableAutoExpand && settings.enableRealtimeCorrections
                  ? 'bg-blue-600'
                  : 'bg-gray-200'
              } ${!settings.enableRealtimeCorrections ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.enableAutoExpand && settings.enableRealtimeCorrections
                    ? 'translate-x-6'
                    : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* AI Streaming */}
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">AI Streaming Corrections</h3>
              <p className="text-sm text-gray-600">
                Apply AI grammar/spelling fixes in real-time
              </p>
              <p className="text-xs text-yellow-600 mt-1">
                ⚠️ Adds 500ms-1s latency • Optional feature
              </p>
            </div>
            <button
              onClick={() => handleToggle('enableAiStreaming')}
              disabled={!settings.enableRealtimeCorrections}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.enableAiStreaming && settings.enableRealtimeCorrections
                  ? 'bg-blue-600'
                  : 'bg-gray-200'
              } ${!settings.enableRealtimeCorrections ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.enableAiStreaming && settings.enableRealtimeCorrections
                    ? 'translate-x-6'
                    : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Visual Feedback Example */}
        {settings.enableAutoExpand && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="text-sm">
              <strong className="text-green-800">Example:</strong>
              <div className="mt-1 text-gray-700">
                You say: <span className="font-mono">"BP 150 over 90"</span>
                <br />
                Display: <span className="font-mono text-green-700">"blood pressure 150/90"</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Auto-expand Vocabulary */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold mb-1 flex items-center">🧠 Auto-expand Vocabulary</h2>
            <p className="text-sm text-gray-600">
              Review built-in abbreviations and toggle automatic expansion per term.
            </p>
            {overridesLocked && (
              <p className="text-xs text-amber-600 mt-2">
                Enable realtime corrections and auto-expand to apply these overrides.
              </p>
            )}
          </div>
          <div className="text-sm text-gray-600">
            Overrides: <span className="font-semibold">{overrideCount}</span>
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={autoExpandSearch}
            onChange={(e) => setAutoExpandSearch(e.target.value)}
            placeholder="Search term or expansion..."
            className="w-full sm:flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex gap-2">
            <button
              onClick={() => setAutoExpandSearch('')}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Clear search
            </button>
            <button
              onClick={handleResetOverrides}
              disabled={overrideCount === 0}
              className={`px-3 py-2 rounded-md ${
                overrideCount === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-red-100 text-red-700 hover:bg-red-200'
              }`}
            >
              Reset overrides
            </button>
          </div>
        </div>

        <div className="mt-4 border border-gray-200 rounded-lg max-h-72 overflow-y-auto">
          {isLoadingAutoExpand ? (
            <div className="p-6 text-center text-gray-500 text-sm">Loading vocabulary…</div>
          ) : filteredAutoExpand.length === 0 ? (
            <div className="p-6 text-center text-gray-500 text-sm">No matching terms.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">
                    Term
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">
                    Expansion
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">
                    Default
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">
                    Override
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAutoExpand.map((entry) => {
                  const canonical = entry.term.toLowerCase();
                  const overrideValue = settings.autoExpandOverrides?.[canonical];
                  const effectiveValue =
                    overrideValue !== undefined ? overrideValue : entry.auto_expand;
                  const hasOverride = overrideValue !== undefined;

                  return (
                    <tr key={entry.term} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-900">
                        {entry.term}
                        {hasOverride && <span className="ml-1 text-xs text-blue-600">*</span>}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{entry.expansion}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            entry.auto_expand
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {entry.auto_expand ? 'On' : 'Off'}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => handleAutoExpandToggle(entry.term, entry.auto_expand)}
                          disabled={overridesLocked}
                          className={`px-3 py-1 rounded-md text-xs font-medium ${
                            effectiveValue
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          } ${overridesLocked ? 'opacity-50 cursor-not-allowed hover:bg-gray-200' : ''}`}
                        >
                          {effectiveValue ? 'Enabled' : 'Disabled'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <p className="mt-3 text-xs text-gray-500">* Override differs from the default behaviour.</p>
      </div>

      {/* Speech Provider */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-2 flex items-center">
          🛰️ Speech Provider
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Choose the streaming engine for live transcription. Azure Speech and Speechmatics are fully managed cloud services, while Sherpa runs locally on your GPU and can leverage custom hotwords from your vocabulary.
        </p>

        <div className="space-y-3">
          <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
            style={{ borderColor: settings.sttProvider === 'azure' ? '#3B82F6' : '#E5E7EB' }}
          >
            <input
              type="radio"
              name="sttProvider"
              value="azure"
              checked={settings.sttProvider === 'azure'}
              onChange={(e) => handleProviderChange(e.target.value)}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900">Azure Speech Service (cloud)</div>
              <div className="text-sm text-gray-600">
                Managed service with automatic scaling. Estimated £1.05/hour realtime, £0.80/hour batch.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
            style={{ borderColor: settings.sttProvider === 'speechmatics' ? '#3B82F6' : '#E5E7EB' }}
          >
            <input
              type="radio"
              name="sttProvider"
              value="speechmatics"
              checked={settings.sttProvider === 'speechmatics'}
              onChange={(e) => handleProviderChange(e.target.value)}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900">Speechmatics (cloud)</div>
              <div className="text-sm text-gray-600">
                Low-latency SaaS transcription with rich language coverage. Configure API key via <code>SPEECHMATICS_API_KEY</code>.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
            style={{ borderColor: settings.sttProvider === 'sherpa' ? '#3B82F6' : '#E5E7EB' }}
          >
            <input
              type="radio"
              name="sttProvider"
              value="sherpa"
              checked={settings.sttProvider === 'sherpa'}
              onChange={(e) => handleProviderChange(e.target.value)}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-gray-900">Sherpa ONNX (local GPU)</div>
              <div className="text-sm text-gray-600">
                Runs inside your infrastructure. £0.00/hour usage, requires NVIDIA GPU and Docker setup.
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Transcription Mode Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          🎙️ Default Transcription Mode
        </h2>
        
        <div className="space-y-3">
          <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
            style={{ borderColor: settings.defaultMode === 'realtime' ? '#3B82F6' : '#E5E7EB' }}
          >
            <input
              type="radio"
              name="mode"
              value="realtime"
              checked={settings.defaultMode === 'realtime'}
              onChange={(e) => handleModeChange(e.target.value)}
              className="mr-3"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">Realtime (Streaming)</div>
              <div className="text-sm text-gray-600">
                {realtimeCostLabel}
              </div>
            </div>
          </label>

          <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
            style={{ borderColor: settings.defaultMode === 'batch' ? '#3B82F6' : '#E5E7EB' }}
          >
            <input
              type="radio"
              name="mode"
              value="batch"
              checked={settings.defaultMode === 'batch'}
              onChange={(e) => handleModeChange(e.target.value)}
              className="mr-3"
            />
            <div className="flex-1">
              <div className="font-medium text-gray-900">Batch (File Upload)</div>
              <div className="text-sm text-gray-600">
                {batchCostLabel}
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Display Settings */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          📊 Display Settings
        </h2>
        
        <div className="space-y-4">
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">Show Confidence Scores</h3>
              <p className="text-sm text-gray-600">
                Display transcription confidence percentage
              </p>
            </div>
            <button
              onClick={() => handleToggle('showConfidenceScores')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.showConfidenceScores ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.showConfidenceScores ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Voice Commands Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">🎤 Voice Commands</h3>
        <div className="text-sm text-blue-800 space-y-1">
          <p>• Say <strong>"correct that"</strong> to fix the last segment</p>
          <p>• Say <strong>"expand that"</strong> to expand abbreviations</p>
          <p>• Say <strong>"spell that"</strong> to spell the last word</p>
          <p className="text-xs text-blue-600 mt-2">
            Voice commands work when real-time corrections are enabled
          </p>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
